import React from "react";
import styled from "@emotion/styled";
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  surface,
  text,
} from "@atlas-int/tokens";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const SubSection = styled.div`
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
`;

const Note = styled.div`
  font-size: ${fontSize.sm};
  color: ${text.muted};
  padding: ${spacing[4]} ${spacing[6]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  margin-top: ${spacing[6]};
`;

const GridDiagram = styled.div`
  font-family: monospace;
  font-size: ${fontSize.sm};
  background-color: ${surface.muted};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  padding: ${spacing[8]};
  line-height: 1.6;
  white-space: pre;
  overflow-x: auto;
`;

// ─── VisualizationLayout Section ────────────────────────────────────────────

const VisualizationLayoutDemo: React.FC = () => (
  <SubSection>
    <SubTitle>VisualizationLayout</SubTitle>
    <Description>
      Composes the standard visualization page structure: title bar (with TotalValue,
      title generator, and FindInViz) plus the main VisualizationGrid. Used by every
      chart page.
    </Description>
    <CodeBlock
      code={`import VisualizationLayout from "visualization/components/VisualizationLayout";

// Props:
interface VisualizationLayoutProps {
  children: React.ReactNode;        // Chart content
  totalValue?: string | number;     // Total value display (bottom-right)
  findInVizOptions?: FindInVizOption[];
  highlightedItem?: string;
  onHighlight?: (item: string | undefined) => void;
}

<VisualizationLayout
  totalValue={formattedTotal}
  findInVizOptions={searchOptions}
  highlightedItem={highlighted}
  onHighlight={setHighlighted}
>
  <TreemapChart data={data} />
</VisualizationLayout>`}
    />
  </SubSection>
);

// ─── VizGrid Section ────────────────────────────────────────────────────────

const VizGridDemo: React.FC = () => (
  <SubSection>
    <SubTitle>VizGrid</SubTitle>
    <Description>
      CSS Grid layout containers for the visualization area. Defines the grid structure
      with named lines and areas for controls, title, content, bottom row, and handles.
    </Description>
    <GridDiagram>{`┌─────────────────────────────────────────────┐
│              ControlsArea                   │  auto
├─────────────────────────────────────────────┤
│          VisualizationTitleArea             │  auto
├─────────────────────────────────────────────┤
│                                             │
│           VisualizationArea                 │
│  ┌───────────────────────────────────────┐  │
│  │     VisualizationContentArea          │  │  1fr
│  ├───────────────────────────────────────┤  │
│  │  VisualizationBottomRowArea           │  │  60px
│  │  (handles)        (total value)       │  │
│  └───────────────────────────────────────┘  │
└─────────────────────────────────────────────┘`}</GridDiagram>
    <CodeBlock
      code={`import {
  VisualizationContainer,
  ControlsContainer,
  VisualizationTitleContainer,
  VisualizationGrid,
  VisualizationContentContainer,
  VisualizationBottomRowContainer,
  VisualizationHandlesContainer,
  VisualizationTotalContainer,
  TotalValueLabel,
  gridAreas,
  gridLines,
} from "visualization/VizGrid";

// VisualizationContainer is the outermost grid:
//   Rows: [auto] controls | [auto] title | [1fr] visualization
//   Columns: [1fr] full width

// VisualizationGrid is the inner grid:
//   Rows: [1fr] content | [60px] bottom row
//   Areas: VisualizationContentArea, VisualizationBottomRowArea`}
    />
  </SubSection>
);

// ─── ControlsGrid Section ───────────────────────────────────────────────────

const ControlsGridDemo: React.FC = () => (
  <SubSection>
    <SubTitle>ControlsGrid</SubTitle>
    <Description>
      CSS Grid layout for the controls/input bar. Defines columns for each control segment:
      product/market selector, dropdowns, year inputs, find-in-viz, and settings.
    </Description>
    <GridDiagram>{`┌──────────┬─────┬───────────┬──────┬───────────┬──────┬──────┬────────┬────────┐
│ Product/ │Arrow│ Dropdown  │ Swap │ Dropdown  │ Year │ Year │ Find   │Settings│
│ Market   │ Icon│   #1      │ Icon │   #2      │  #1  │  #2  │ in Viz │        │
│ Selector │     │           │      │           │      │      │        │        │
│  180px   │55px │  275px    │ 55px │  275px    │ 85px │ 85px │  1fr   │  1fr   │
├──────────┴─────┴───────────┴──────┴───────────┴──────┴──────┴────────┴────────┤
│ Labels row (20px) then Inputs row (34px) then Title row (34px)                │
└───────────────────────────────────────────────────────────────────────────────┘`}</GridDiagram>
    <CodeBlock
      code={`import {
  ControlsGrid,
  LabelContainer,
  InputContainer,
  DropdownInputContainer,
  YearInputContainer,
  IconContainer,
  SegmentContainer,
} from "@atlas-int/controls";

// Grid rows: labels (20px) | inputs (34px) | title (34px)
// Each column is a named grid area for precise placement.`}
    />
    <Note>
      ControlsGrid is used inside ControlsContainer from VizGrid. The named grid
      areas allow each control to be placed independently via CSS grid-area.
    </Note>
  </SubSection>
);

// ─── Main Section ────────────────────────────────────────────────────────────

const LayoutSection: React.FC = () => (
  <PreviewSection
    id="layout"
    title="Layout Primitives"
    description="VizGrid, ControlsGrid, VisualizationLayout"
  >
    <VisualizationLayoutDemo />
    <VizGridDemo />
    <ControlsGridDemo />
  </PreviewSection>
);

export default LayoutSection;
